import { useState, useEffect, useCallback, useRef } from 'react'
import { FileText, ChevronDown, ChevronLeft, ChevronRight, Printer, RefreshCw, Calendar } from 'lucide-react'
import { downloadPdf } from '../../utils/pdfUtils'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { gestaoService } from '../../services/gestaoService'
import leiloesService from '../../services/leiloesService'
import toast from 'react-hot-toast'

const fmt = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString('pt-BR') : '—')

// ── Date helpers ──────────────────────────────────────────────────────────────
const isoToDisplay = (iso) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}` }
const isoToDate = (iso) => { if (!iso) return null; const [y, m, d] = iso.split('-'); return new Date(Number(y), Number(m) - 1, Number(d)) }
const dateToISO = (date) => { if (!date) return ''; return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` }
const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }
const MONTHS_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
const DAYS_PT   = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb']

function DateRangePicker({ dataInicio, dataFim, onChange }) {
  const today = new Date(); today.setHours(0,0,0,0)
  const [open, setOpen]           = useState(false)
  const [pos, setPos]             = useState({ top: 0, left: 0 })
  const [tempStart, setTempStart] = useState(null)
  const [tempEnd, setTempEnd]     = useState(null)
  const [selecting, setSelecting] = useState('start')
  const [hoverDate, setHoverDate] = useState(null)
  const [months, setMonths]       = useState(() => {
    const m = today.getMonth(), y = today.getFullYear()
    return { left: { year: m===0?y-1:y, month: m===0?11:m-1 }, right: { year: y, month: m } }
  })
  const triggerRef = useRef(null)
  const popoverRef = useRef(null)
  useEffect(() => {
    if (!open) return
    const h = (e) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target) &&
          popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])
  const openPicker = () => {
    if (triggerRef.current) { const r = triggerRef.current.getBoundingClientRect(); setPos({ top: r.bottom+6, left: r.left }) }
    setTempStart(isoToDate(dataInicio)); setTempEnd(isoToDate(dataFim)); setSelecting('start'); setOpen(true)
  }
  const shiftMonths = (delta) => setMonths(({ left, right }) => {
    const shift = ({ year, month }) => { const m = month+delta; if (m<0) return {year:year-1,month:11}; if (m>11) return {year:year+1,month:0}; return {year,month:m} }
    return { left: shift(left), right: shift(right) }
  })
  const handleDayClick = (date) => {
    if (selecting==='start' || !tempStart || date<tempStart) { setTempStart(date); setTempEnd(null); setSelecting('end') }
    else { setTempEnd(date); setSelecting('start') }
  }
  const applyPreset = (s, e) => { setTempStart(s); setTempEnd(e); setSelecting('start') }
  const handleApply = () => { if (!tempStart) return; onChange(dateToISO(tempStart), dateToISO(tempEnd||tempStart)); setOpen(false) }
  const presets = [
    { label: 'Hoje',           fn: () => applyPreset(today, today) },
    { label: 'Ontem',          fn: () => { const d=addDays(today,-1); applyPreset(d,d) } },
    { label: 'Últimos 7 dias', fn: () => applyPreset(addDays(today,-6), today) },
    { label: 'Últimos 30 dias',fn: () => applyPreset(addDays(today,-29), today) },
    { label: 'Este mês',       fn: () => applyPreset(new Date(today.getFullYear(),today.getMonth(),1), today) },
    { label: 'Mês passado',    fn: () => applyPreset(new Date(today.getFullYear(),today.getMonth()-1,1), new Date(today.getFullYear(),today.getMonth(),0)) },
    { label: 'Este ano',       fn: () => applyPreset(new Date(today.getFullYear(),0,1), today) },
    { label: 'Ano passado',    fn: () => applyPreset(new Date(today.getFullYear()-1,0,1), new Date(today.getFullYear()-1,11,31)) },
  ]
  const renderMonth = (year, month) => {
    const firstDow=new Date(year,month,1).getDay(), daysInMonth=new Date(year,month+1,0).getDate()
    const cells=[]; for(let i=0;i<firstDow;i++) cells.push(null); for(let d=1;d<=daysInMonth;d++) cells.push(new Date(year,month,d))
    const effectiveEnd = tempEnd || (selecting==='end' && hoverDate ? hoverDate : null)
    return (
      <div className="w-52">
        <div className="grid grid-cols-7 mb-1">{DAYS_PT.map(d=><div key={d} className="text-center text-[11px] text-gray-400 font-medium pb-1">{d}</div>)}</div>
        <div className="grid grid-cols-7">{cells.map((date,i)=>{
          if(!date) return <div key={`e${i}`}/>
          const iso=dateToISO(date), isStart=tempStart&&dateToISO(tempStart)===iso, isEnd=tempEnd&&dateToISO(tempEnd)===iso
          const inRange=tempStart&&effectiveEnd&&date>tempStart&&date<effectiveEnd, isToday=iso===dateToISO(today)
          return <div key={iso} onClick={()=>handleDayClick(date)} onMouseEnter={()=>selecting==='end'&&setHoverDate(date)}
            className={['text-center text-sm py-[5px] cursor-pointer select-none transition-colors',
              isStart||isEnd?'bg-indigo-600 text-white rounded-full font-semibold':inRange?'bg-indigo-100 text-indigo-800':isToday?'text-indigo-600 font-semibold hover:bg-gray-100 rounded-full':'text-gray-700 hover:bg-gray-100 rounded-full'].join(' ')}>{date.getDate()}</div>
        })}</div>
      </div>
    )
  }
  return (
    <div>
      <button ref={triggerRef} onClick={openPicker}
        className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-sm bg-white transition-colors whitespace-nowrap ${open?'border-indigo-400 ring-1 ring-indigo-200':'border-gray-200 hover:border-gray-300'}`}>
        <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0"/>
        <span className={dataInicio?'text-gray-800':'text-gray-400'}>{dataInicio?`${isoToDisplay(dataInicio)} – ${isoToDisplay(dataFim||dataInicio)}`:'Selecionar período'}</span>
      </button>
      {open&&<div ref={popoverRef} style={{position:'fixed',top:pos.top,left:pos.left,zIndex:9999}} className="bg-white rounded-xl shadow-2xl border border-gray-100 p-5 flex gap-0">
        <div className="flex flex-col gap-0.5 pr-4 border-r border-gray-100 mr-5 min-w-[130px]">
          {presets.map(p=><button key={p.label} onClick={p.fn} className="text-left text-sm px-3 py-2 rounded-lg hover:bg-indigo-50 hover:text-indigo-700 text-gray-600 transition-colors whitespace-nowrap">{p.label}</button>)}
        </div>
        <div className="flex flex-col">
          <div className="flex items-center justify-between mb-3">
            <button onClick={()=>shiftMonths(-1)} className="p-1 hover:bg-gray-100 rounded text-gray-500"><ChevronLeft className="w-4 h-4"/></button>
            <div className="flex gap-8">
              <span className="text-sm font-semibold text-gray-800 w-48 text-center">{MONTHS_PT[months.left.month]} {months.left.year}</span>
              <span className="text-sm font-semibold text-gray-800 w-48 text-center">{MONTHS_PT[months.right.month]} {months.right.year}</span>
            </div>
            <button onClick={()=>shiftMonths(1)} className="p-1 hover:bg-gray-100 rounded text-gray-500"><ChevronRight className="w-4 h-4"/></button>
          </div>
          <div className="flex gap-5" onMouseLeave={()=>setHoverDate(null)}>
            {renderMonth(months.left.year,months.left.month)}
            <div className="w-px bg-gray-100 self-stretch"/>
            {renderMonth(months.right.year,months.right.month)}
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-500">{tempStart?`${isoToDisplay(dateToISO(tempStart))} – ${tempEnd?isoToDisplay(dateToISO(tempEnd)):'...'}`:'Selecione o período'}</span>
            <div className="flex gap-2">
              <button onClick={()=>setOpen(false)} className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancelar</button>
              <button onClick={handleApply} disabled={!tempStart} className="px-4 py-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-40 font-medium">Aplicar</button>
            </div>
          </div>
        </div>
      </div>}
    </div>
  )
}

export default function RelatorioContasPagar() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const iframeRef = useRef(null)

  const [leiloes, setLeiloes] = useState([])
  const [modoPeriodo, setModoPeriodo] = useState(false)
  const [leilaoId, setLeilaoId] = useState('')
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [statusFiltro, setStatusFiltro] = useState('')
  const [categoriaFiltro, setCategoriaFiltro] = useState('')
  const [categorias, setCategorias] = useState([])
  const [contas, setContas] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdfModal, setShowPdfModal] = useState(false)

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    Promise.all([
      leiloesService.getByOrganizador(orgId),
      gestaoService.listarCategorias(orgId, 'despesa'),
    ]).then(([data, cats]) => {
      const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
      setLeiloes(sorted)
      setCategorias(cats.data || [])
      if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
    }).catch(() => {}).finally(() => setLoadingL(false))
  }, [orgId])

  const carregar = useCallback(async () => {
    if (!orgId) return
    setLoading(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      if (statusFiltro) filtros.status = statusFiltro
      if (categoriaFiltro) filtros.categoria_id = categoriaFiltro
      const res = await gestaoService.listarContasPagar(orgId, filtros)
      setContas(res.data || [])
    } catch {
      toast.error('Erro ao carregar contas')
    } finally {
      setLoading(false)
    }
  }, [orgId, modoPeriodo, leilaoId, dataInicio, dataFim, statusFiltro, categoriaFiltro])

  useEffect(() => {
    if (orgId && (leilaoId || modoPeriodo)) carregar()
  }, [orgId, leilaoId]) // eslint-disable-line

  const handleImprimir = async () => {
    if (!orgId) return
    setLoadingPdf(true)
    try {
      const filtros = {}
      if (!modoPeriodo && leilaoId) filtros.leilao_id = leilaoId
      if (modoPeriodo && dataInicio) filtros.data_inicio = dataInicio
      if (modoPeriodo && dataFim) filtros.data_fim = dataFim
      if (statusFiltro) filtros.status = statusFiltro
      if (categoriaFiltro) filtros.categoria_id = categoriaFiltro
      const res = await gestaoService.relatorioContasPagarPdf(orgId, filtros)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(blob)
      setPdfUrl(url)
      setShowPdfModal(true)
    } catch {
      toast.error('Erro ao gerar PDF')
    } finally {
      setLoadingPdf(false)
    }
  }

  const closePdfModal = () => { setShowPdfModal(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  // Separar contas por status
  const emAberto = contas.filter(c => c.status === 'em_aberto')
  const atrasado = contas.filter(c => c.status === 'atrasado')
  const pago = contas.filter(c => c.status === 'pago')

  const totalGeral = contas.reduce((s, c) => s + Number(c.valor || 0), 0)
  const totalAberto = emAberto.reduce((s, c) => s + Number(c.valor || 0), 0)
  const totalAtrasado = atrasado.reduce((s, c) => s + Number(c.valor || 0), 0)
  const totalPago = pago.reduce((s, c) => s + Number(c.valor_pago || c.valor_recebido || 0), 0)

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const filtroInfo = modoPeriodo
    ? `Período: ${dataInicio ? fmtDate(dataInicio) : '...'} a ${dataFim ? fmtDate(dataFim) : '...'}`
    : leilaoAtual ? leilaoAtual.nome : 'Todos os leilões'

  if (!orgId) return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
      Selecione um organizador para gerar o relatório.
    </div>
  )

  return (
    <div className="space-y-4">
      {/* Título */}
      <div className="no-print flex items-center gap-2">
        <FileText className="w-5 h-5 text-red-600" />
        <h1 className="text-lg font-semibold text-gray-800">Relatório de Despesas do Leilão</h1>
      </div>

      {/* Filtros */}
      <div className="no-print bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex rounded-lg overflow-hidden border border-gray-200">
            <button onClick={() => setModoPeriodo(false)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${!modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Leilão
            </button>
            <button onClick={() => setModoPeriodo(true)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${modoPeriodo ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
              Por Período
            </button>
          </div>

          {!modoPeriodo ? (
            <div className="flex flex-col gap-1 min-w-[220px]">
              <label className="text-xs text-gray-500 font-medium">Leilão</label>
              <div className="relative">
                <select value={leilaoId} onChange={e => setLeilaoId(e.target.value)} disabled={loadingL}
                  className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100">
                  <option value="">Todos os leilões</option>
                  {leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>
            </div>
          ) : (
            <DateRangePicker
              dataInicio={dataInicio}
              dataFim={dataFim}
              onChange={(s, e) => { setDataInicio(s); setDataFim(e) }}
            />
          )}

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Status</label>
            <select value={statusFiltro} onChange={e => setStatusFiltro(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm">
              <option value="">Todos</option>
              <option value="em_aberto">Em Aberto</option>
              <option value="atrasado">Atrasado</option>
              <option value="pago">Pago</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Categoria</label>
            <select value={categoriaFiltro} onChange={e => setCategoriaFiltro(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm min-w-[150px]">
              <option value="">Todas</option>
              {categorias.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>

          <button onClick={carregar}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
            Filtrar
          </button>

          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            <button onClick={handleImprimir} disabled={!contas.length || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir / PDF'}
            </button>
          </div>
        </div>
      </div>

      {/* Conteúdo imprimível */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600" />
        </div>
      ) : contas.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">
          Nenhuma conta encontrada com os filtros selecionados
        </div>
      ) : (
        <div className="print-page bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          {/* Cabeçalho */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-gray-800 gap-4">
            <div className="w-28 flex-shrink-0">
              {organizadorAtivo?.logo_url
                ? <img src={organizadorAtivo.logo_url} alt="Logo" className="max-h-14 max-w-full object-contain" />
                : <span className="text-xs font-bold text-gray-700">{organizadorAtivo?.name}</span>}
            </div>
            <div className="text-center flex-1">
              <h2 className="text-xl font-black uppercase tracking-wide text-gray-900">Relatório de Despesas do Leilão</h2>
              <p className="text-sm text-gray-600 mt-0.5">{organizadorAtivo?.name}</p>
              <p className="text-sm text-gray-500 mt-0.5">{filtroInfo}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                Emitido em {new Date().toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })} às {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
              </p>
            </div>
            <div className="w-28 flex-shrink-0" />
          </div>

          {/* Cards resumo */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <p className="text-xs text-blue-600 font-medium">Total Geral</p>
              <p className="text-lg font-bold text-blue-800">{fmt(totalGeral)}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <p className="text-xs text-yellow-600 font-medium">Em Aberto</p>
              <p className="text-lg font-bold text-yellow-800">{fmt(totalAberto)}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-xs text-red-600 font-medium">Atrasado</p>
              <p className="text-lg font-bold text-red-800">{fmt(totalAtrasado)}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <p className="text-xs text-green-600 font-medium">Pago</p>
              <p className="text-lg font-bold text-green-800">{fmt(totalPago)}</p>
            </div>
          </div>

          {/* Seção EM ABERTO */}
          {emAberto.length > 0 && (
            <div className="mb-8">
              <div className="bg-yellow-100 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-yellow-800 uppercase">
                  Em Aberto ({emAberto.length} {emAberto.length === 1 ? 'conta' : 'contas'})
                </h3>
              </div>
              <table className="w-full text-left border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Vencimento</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Descrição</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Fornecedor</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Categoria</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Forma Pgto</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {emAberto.map((c, i) => (
                    <tr key={c.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-xs text-gray-700">{fmtDate(c.data_vencimento)}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.descricao}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.cliente?.nome || c.fornecedor || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.categoria?.nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.forma_pagamento?.nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-800 font-semibold text-right">{fmt(c.valor)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-yellow-50 border-t border-gray-300">
                  <tr>
                    <td colSpan={5} className="px-3 py-2 text-xs font-bold text-gray-700 text-right">Total Em Aberto:</td>
                    <td className="px-3 py-2 text-xs font-bold text-yellow-800 text-right">{fmt(totalAberto)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Seção ATRASADO */}
          {atrasado.length > 0 && (
            <div className="mb-8">
              <div className="bg-red-100 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-red-800 uppercase">
                  Atrasado ({atrasado.length} {atrasado.length === 1 ? 'conta' : 'contas'})
                </h3>
              </div>
              <table className="w-full text-left border border-red-200">
                <thead className="bg-red-50">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-red-700">Vencimento</th>
                    <th className="px-3 py-2 text-xs font-semibold text-red-700">Descrição</th>
                    <th className="px-3 py-2 text-xs font-semibold text-red-700">Fornecedor</th>
                    <th className="px-3 py-2 text-xs font-semibold text-red-700">Categoria</th>
                    <th className="px-3 py-2 text-xs font-semibold text-red-700">Forma Pgto</th>
                    <th className="px-3 py-2 text-xs font-semibold text-red-700 text-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {atrasado.map((c, i) => (
                    <tr key={c.id} className={i % 2 === 0 ? 'bg-white' : 'bg-red-50/30'}>
                      <td className="px-3 py-2 text-xs text-red-700">{fmtDate(c.data_vencimento)}</td>
                      <td className="px-3 py-2 text-xs text-red-700">{c.descricao}</td>
                      <td className="px-3 py-2 text-xs text-red-700">{c.cliente?.nome || c.fornecedor || '—'}</td>
                      <td className="px-3 py-2 text-xs text-red-700">{c.categoria?.nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-red-700">{c.forma_pagamento?.nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-red-800 font-semibold text-right">{fmt(c.valor)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-red-100 border-t border-red-300">
                  <tr>
                    <td colSpan={5} className="px-3 py-2 text-xs font-bold text-red-700 text-right">Total Atrasado:</td>
                    <td className="px-3 py-2 text-xs font-bold text-red-800 text-right">{fmt(totalAtrasado)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Seção PAGO */}
          {pago.length > 0 && (
            <div className="mb-8">
              <div className="bg-green-100 px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold text-green-800 uppercase">
                  Pago ({pago.length} {pago.length === 1 ? 'conta' : 'contas'})
                </h3>
              </div>
              <table className="w-full text-left border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Vencimento</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Descrição</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Fornecedor</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Categoria</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Forma Pgto</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600">Data Baixa</th>
                    <th className="px-3 py-2 text-xs font-semibold text-gray-600 text-right">Valor Pago</th>
                  </tr>
                </thead>
                <tbody>
                  {pago.map((c, i) => (
                    <tr key={c.id} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 text-xs text-gray-700">{fmtDate(c.data_vencimento)}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.descricao}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.cliente?.nome || c.fornecedor || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.categoria?.nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{c.forma_pagamento?.nome || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">{fmtDate(c.data_baixa)}</td>
                      <td className="px-3 py-2 text-xs text-green-700 font-semibold text-right">{fmt(c.valor_pago || c.valor_recebido)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-green-50 border-t border-gray-300">
                  <tr>
                    <td colSpan={6} className="px-3 py-2 text-xs font-bold text-gray-700 text-right">Total Pago:</td>
                    <td className="px-3 py-2 text-xs font-bold text-green-800 text-right">{fmt(totalPago)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal PDF */}
      {showPdfModal && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Relatório de Despesas do Leilão</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, 'Relatorio_Contas_Pagar.pdf')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">
                  Salvar PDF
                </button>
                <button onClick={printPdf}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                  <Printer className="w-4 h-4" /> Imprimir
                </button>
                <button onClick={closePdfModal}
                  className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">
                  Fechar
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl">
              <iframe ref={iframeRef} src={pdfUrl} title="Relatório Despesas do Leilão"
                style={{ width: '100%', height: '100%', border: 'none' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
